import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, Clock, Loader2, Sparkles, TrendingUp, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { readingService } from "@/services/readingService";
import type { ReadingArticle, ReadingProgress } from "@/services/readingService";

export default function ReadingList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<ReadingArticle[]>([]);
  const [progress, setProgress] = useState<Record<string, ReadingProgress>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [articlesData, progressData] = await Promise.all([
          readingService.getArticles(),
          readingService.fetchUserProgress()
        ]);
        
        setArticles(articlesData);

        // Map progress by article ID
        const progressMap: Record<string, ReadingProgress> = {};
        progressData.forEach(p => {
          progressMap[p.article_id] = p;
        });
        setProgress(progressMap);

      } catch (err) {
        console.error("Failed to load reading catalog:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const getLevelColor = (level: string) => {
    if (level.includes("A")) return "bg-emerald-100 text-emerald-700";
    if (level.includes("B")) return "bg-amber-100 text-amber-700";
    if (level.includes("C")) return "bg-rose-100 text-rose-700";
    return "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-32 h-full">
        <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mb-4" />
        <h2 className="text-xl font-bold text-slate-700">Carregando artigos...</h2>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in duration-500 pb-24">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-slate-200">
        <div className="max-w-2xl space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-sm border border-indigo-100">
              <BookOpen className="w-5 h-5" />
            </span>
            <Badge className="bg-indigo-100 text-indigo-800 border-none font-bold text-[10px] tracking-wider uppercase">
              Leitura e Vocabulário
            </Badge>
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900">
            Coin Lecture
          </h1>
          <p className="text-slate-500 text-lg">
            Expanda seu vocabulário e treine sua compreensão escrita com artigos autênticos e testes de interpretação.
          </p>
        </div>
      </div>

      {articles.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-slate-200 shadow-sm">
          <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-800">Nenhum artigo disponível no momento.</h3>
          <p className="text-slate-500 mt-2">Em breve adicionaremos conteúdos aqui para você praticar!</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {articles.map((article) => {
            const userStatus = progress[article.id];
            const isCompleted = userStatus?.status === "completed";

            return (
              <Card 
                key={article.id} 
                className={`flex flex-col justify-between overflow-hidden transition-all duration-300 border shadow-sm hover:shadow-md ${
                  isCompleted ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200 hover:border-indigo-300"
                }`}
              >
                <CardHeader className="p-5 pb-3">
                  <div className="flex justify-between items-start mb-3 gap-2">
                    <Badge variant="outline" className="font-bold text-xs bg-white text-slate-600">
                      {article.category}
                    </Badge>
                    {isCompleted && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-none px-2 shadow-xs flex gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Concluído
                      </Badge>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 leading-tight line-clamp-2">
                    {article.title}
                  </h3>
                </CardHeader>
                
                <CardContent className="p-5 py-2 flex-1">
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <span className={`inline-flex items-center rounded-lg px-2 py-1 text-xs font-bold ${getLevelColor(article.level)}`}>
                      <TrendingUp className="w-3.5 h-3.5 mr-1" />
                      Nível {article.level}
                    </span>
                    <span className="inline-flex items-center text-xs font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-lg">
                      <Clock className="w-3.5 h-3.5 mr-1" />
                      {article.estimated_time}
                    </span>
                  </div>
                </CardContent>

                <CardFooter className="p-5 pt-4 border-t border-slate-50">
                  <Button 
                    onClick={() => navigate(`/reading/${article.slug}`)}
                    className={`w-full font-bold shadow-sm ${
                      isCompleted 
                        ? "bg-white text-slate-700 border border-slate-200 hover:bg-slate-100" 
                        : "bg-indigo-600 text-white hover:bg-indigo-700"
                    }`}
                  >
                    {isCompleted ? "Revisar Leitura" : "Começar Leitura"}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
